import 'twin.macro';
import { XMarkIcon } from '@heroicons/react/24/outline';

import React, { useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { Notation } from '@teetsh/app/src/types/notationSystem';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { PlusIcon as Plus } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';
import Input from 'components/Form/Input2';
import { NotationRangeIndicator } from './NotationRangeIndicator';
import {
  adjustNotationValuesForNewMax,
  redistributeNotationValues,
} from './adjustNotationValues';

interface Props {
  notations: Notation[];
  onChange: (v: Notation[]) => void;
}

export function NotationsLabel(props: Props) {
  const { notations, onChange } = props;
  const { t } = useTranslation(['userSettings', 'common']);
  const newNotationIdRef = useRef<string | null>(null);
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    if (
      newNotationIdRef.current &&
      inputRefs.current[newNotationIdRef.current]
    ) {
      inputRefs.current[newNotationIdRef.current]?.focus();
      newNotationIdRef.current = null;
    }
  }, [notations]);

  return (
    <div>
      <div tw=" flex gap-x-3 mb-3">
        <NotationRangeIndicator />
        <div id="notation-labels" tw="space-y-3 flex-1">
          {notations.map(v => {
            if (v.value <= 0) {
              return null;
            }
            return (
              <div tw="flex items-center gap-x-2" key={v.id}>
                <Input
                  fill
                  inputRef={(el: HTMLInputElement | null) => {
                    inputRefs.current[v.id] = el;
                  }}
                  value={v.label}
                  onChange={e => {
                    const updatedNotations = notations.map(n =>
                      n.id === v.id ? { ...n, label: e.target.value } : n,
                    );
                    onChange(updatedNotations);
                  }}
                  tw="flex-1"
                />
                <div>
                  <Button
                    minimal
                    onClick={() => {
                      const remainingNotations = notations.filter(
                        notation => notation.id !== v.id,
                      );
                      const redistributed =
                        redistributeNotationValues(remainingNotations);
                      onChange(redistributed);
                    }}
                    icon={XMarkIcon}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Button
        onClick={() => {
          const newId = uuid();
          const newNotation = {
            id: newId,
            value: 100,
            label: '',
          };
          newNotationIdRef.current = newId;
          const adjustedNotations = adjustNotationValuesForNewMax(
            notations,
            newNotation,
          );
          onChange(adjustedNotations);
        }}
        tw="ml-8"
        icon={Plus}
      >
        {t('userSettings:notationSystems.addNewNote')}
      </Button>
    </div>
  );
}
